import React, { useState, useEffect, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  CartesianGrid, Cell 
} from 'recharts';
import { X, ExternalLink, AlertTriangle, ChevronDown } from 'lucide-react';

// --- CONFIGURATION ---
const API_BASE = '/api/duty/api/v1'; 
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const AVAILABLE_PRODUCTS = [
  { id: 'rtx-5090', name: 'GeForce RTX 5090', minThreshold: 1000 },
  { id: 'iphone-16-pro', name: 'iPhone 16 Pro', minThreshold: 500 },
  { id: 'ps5-pro', name: 'PlayStation 5 Pro', minThreshold: 800 },
  { id: 'chanel-255', name: 'Chanel 2.55', minThreshold: 800 },
  { id: 'macbook-pro-m4', name: 'MacBook Pro M4', minThreshold: 800 }
];

const RATES = {
  'USD': 1.0, 'EUR': 1.08, 'BRL': 0.175, 'GBP': 1.26, 
  'INR': 0.012, 'IDR': 0.000064, 'JPY': 0.0067,
};

// --- PARSING ENGINE ---
const normalizePrice = (snapshot, country) => {
  let raw = snapshot.price;
  let currency = snapshot.currency || (country === 'US' ? 'USD' : 'EUR');
  
  if (['BR', 'ID', 'DE', 'FR', 'TR'].includes(country) && raw < 1000) {
     raw = raw * 1000;
  }

  const rate = RATES[currency] || 1;
  const usdPrice = raw * rate;

  return {
    ...snapshot,
    normalizedNative: raw,
    usdPrice: usdPrice,
    currency: currency
  };
};

const calculateStats = (snapshots, country, minThreshold) => {
  if (!snapshots || !Array.isArray(snapshots)) return null;

  const cleanData = snapshots
    .map(s => normalizePrice(s, country))
    .filter(s => s.usdPrice > minThreshold);

  if (cleanData.length === 0) return null;

  cleanData.sort((a, b) => a.usdPrice - b.usdPrice);

  const mid = Math.floor(cleanData.length / 2);
  const median = cleanData.length % 2 !== 0 
    ? cleanData[mid].usdPrice 
    : (cleanData[mid - 1].usdPrice + cleanData[mid].usdPrice) / 2;

  return {
    country,
    median,
    min: cleanData[0].usdPrice,
    max: cleanData[cleanData.length - 1].usdPrice,
    offers: cleanData,
    count: cleanData.length
  };
};

const FreeShopIndicator = () => {
  const [loading, setLoading] = useState(true);
  const [marketData, setMarketData] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(AVAILABLE_PRODUCTS[0].id);

  const theme = {
    bg: '#09090b',
    card: '#18181b',
    border: '#27272a',
    text: '#e4e4e7',
    muted: '#a1a1aa',
    accent: '#3b82f6',
    success: '#10b981',
    danger: '#ef4444',
  };

  useEffect(() => {
    fetchGlobalData();
  }, [selectedProductId]);

  const fetchGlobalData = async () => {
    setLoading(true);
    // Note: We don't clear selectedCountry here anymore so the UI doesn't 
    // flicker to "empty state" during the load if we can avoid it.
    
    const countries = ['US', 'BR', 'FR', 'DE', 'GB', 'ID', 'JP']; 
    const results = [];
    const currentProduct = AVAILABLE_PRODUCTS.find(p => p.id === selectedProductId);

    for (const country of countries) {
      try {
        const res = await fetch(`${API_BASE}/prices/${selectedProductId}/${country}`);
        if (!res.ok) throw new Error('Network error');
        const json = await res.json();
        
        const stats = calculateStats(json.snapshots || [], country, currentProduct.minThreshold);
        if (stats) results.push(stats);
      } catch (err) {
        console.warn(`Skipping ${country} for ${selectedProductId}:`, err);
      }
    }

    const sortedResults = results.sort((a, b) => a.median - b.median);
    setMarketData(sortedResults);

    // --- AUTO-OPEN LOGIC ---
    // Sets the side menu to the cheapest market automatically
    if (sortedResults.length > 0) {
      setSelectedCountry(sortedResults[0]);
    } else {
      setSelectedCountry(null);
    }

    setLoading(false);
  };

  const colorScale = useMemo(() => {
    if (!marketData.length) return () => '#333';
    const values = marketData.map(d => d.median);
    return scaleLinear()
      .domain([Math.min(...values), Math.max(...values)])
      .range([theme.success, theme.danger]);
  }, [marketData]);

  if (loading) return (
    <div style={{background: theme.bg, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.text}}>
      <div style={{textAlign: 'center'}}>
        <div style={{marginBottom: 16, fontSize: '1.1rem', fontWeight: 500}}>Scanning Global Markets...</div>
        <div style={{fontSize: '0.8rem', color: theme.muted}}>Fetching {selectedProductId} pricing data</div>
      </div>
    </div>
  );

  const cheapest = marketData[0];
  const avgMedian = marketData.length ? Math.round(marketData.reduce((a, b) => a + b.median, 0) / marketData.length) : 0;

  return (
    <div style={{ background: theme.bg, minHeight: '100vh', color: theme.text, padding: 24, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      
      {/* HEADER & SELECTOR */}
      <div style={{ marginBottom: 24, borderBottom: `1px solid ${theme.border}`, paddingBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 12 }}>
            Global Arbitrage 
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <select 
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                style={{
                  appearance: 'none',
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: theme.accent,
                  border: `1px solid ${theme.accent}`,
                  padding: '4px 32px 4px 12px',
                  borderRadius: 6,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                {AVAILABLE_PRODUCTS.map(p => <option key={p.id} value={p.id} style={{background: theme.card, color: theme.text}}>{p.name}</option>)}
              </select>
              <ChevronDown size={16} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: theme.accent }} />
            </div>
          </h1>
          <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
             <div style={{ fontSize: '0.9rem', color: theme.muted }}>
               Global Median: <b style={{color: theme.text}}>${avgMedian}</b>
             </div>
             {cheapest && (
               <div style={{ fontSize: '0.9rem', color: theme.muted }}>
                 Best Market: <b style={{color: theme.success}}>{cheapest.country} (${Math.round(cheapest.median)})</b>
               </div>
             )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        
        {/* LEFT: MAP & TABLE */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ background: theme.card, borderRadius: 12, border: `1px solid ${theme.border}`, height: 400, overflow: 'hidden', position: 'relative' }}>
             <ComposableMap projectionConfig={{ scale: 160 }} style={{ width: "100%", height: "100%" }}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) => geographies.map(geo => (
                    <Geography key={geo.rsmKey} geography={geo} fill="#212124" stroke="#09090b" strokeWidth={0.5} />
                  ))}
                </Geographies>
                {marketData.map(d => (
                  <Marker key={d.country} coordinates={getCoords(d.country)} onClick={() => setSelectedCountry(d)} style={{cursor: 'pointer'}}>
                    <circle r={8} fill={colorScale(d.median)} stroke="white" strokeWidth={2} />
                    <text textAnchor="middle" y={-15} style={{ fill: 'white', fontSize: 11, fontWeight: '600', textShadow: '0px 1px 3px black' }}>{d.country}</text>
                  </Marker>
                ))}
             </ComposableMap>
          </div>

          <div style={{ background: theme.card, borderRadius: 12, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${theme.border}` }}>
                  <th style={{ padding: 12, textAlign: 'left', color: theme.muted }}>Market</th>
                  <th style={{ padding: 12, textAlign: 'right', color: theme.muted }}>Min</th>
                  <th style={{ padding: 12, textAlign: 'right', color: theme.accent }}>Median (USD)</th>
                  <th style={{ padding: 12, textAlign: 'right', color: theme.muted }}>Max</th>
                  <th style={{ padding: 12, textAlign: 'right', color: theme.muted }}>Delta</th>
                </tr>
              </thead>
              <tbody>
                {marketData.map(row => {
                  const arb = ((row.median / (cheapest?.median || 1)) - 1) * 100;
                  const isSelected = selectedCountry?.country === row.country;
                  return (
                    <tr key={row.country} onClick={() => setSelectedCountry(row)}
                      style={{ cursor: 'pointer', borderBottom: `1px solid ${theme.border}`, background: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent' }}>
                      <td style={{ padding: 12, fontWeight: 'bold' }}>{getFlag(row.country)} {row.country}</td>
                      <td style={{ padding: 12, textAlign: 'right', fontFamily: 'monospace' }}>${Math.round(row.min)}</td>
                      <td style={{ padding: 12, textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', color: theme.accent }}>${Math.round(row.median)}</td>
                      <td style={{ padding: 12, textAlign: 'right', fontFamily: 'monospace' }}>${Math.round(row.max)}</td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        <span style={{ padding: '2px 6px', borderRadius: 4, color: arb > 0 ? theme.danger : theme.success, background: arb > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)' }}>
                          {arb > 0 ? '+' : ''}{arb.toFixed(0)}%
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT: DETAIL PANEL */}
        <div style={{ position: 'relative' }}>
          {selectedCountry ? (
            <div 
              key={`${selectedProductId}-${selectedCountry.country}`}
              style={{ background: theme.card, borderRadius: 12, border: `1px solid ${theme.border}`, padding: 20, position: 'sticky', top: 20, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                 <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{getFlag(selectedCountry.country)} {selectedCountry.country} Listings</h2>
                 <button onClick={() => setSelectedCountry(null)} style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer' }}><X size={20}/></button>
               </div>

               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                  <StatBox label="MEDIAN (USD)" value={`$${Math.round(selectedCountry.median)}`} color={theme.accent} theme={theme} />
                  <StatBox label="LISTINGS" value={selectedCountry.count} theme={theme} />
               </div>

               <div style={{ flex: '0 0 180px', marginBottom: 20 }}>
                 <ResponsiveContainer width="100%" height="100%">
                   <BarChart data={generateHistogram(selectedCountry.offers)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                      <XAxis dataKey="range" hide />
                      <Tooltip cursor={{fill: 'rgba(255,255,255,0.05)'}} contentStyle={{ background: '#18181b', border: '1px solid #333' }} labelStyle={{color: '#fff'}} />
                      <Bar dataKey="count" fill={theme.accent} radius={[4, 4, 0, 0]} />
                   </BarChart>
                 </ResponsiveContainer>
               </div>

               <div style={{ flex: 1, overflowY: 'auto', borderTop: `1px solid ${theme.border}` }}>
                 {selectedCountry.offers.map((o, i) => (
                   <div key={i} style={{ padding: '12px 0', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ overflow: 'hidden' }}>
                         <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: theme.text }}>{o.retailer || 'Retailer'}</div>
                         <div style={{ fontSize: '0.75rem', color: theme.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.title || 'Product Listing'}</div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: 90 }}>
                         <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: theme.success }}>${Math.round(o.usdPrice)}</div>
                         <div style={{ fontSize: '0.65rem', color: theme.muted }}>{o.currency} {o.normalizedNative.toLocaleString()}</div>
                      </div>
                   </div>
                 ))}
               </div>
            </div>
          ) : (
            <div style={{ height: '100%', border: `1px dashed ${theme.border}`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.muted, flexDirection: 'column', gap: 12, minHeight: 400 }}>
              <AlertTriangle size={32} opacity={0.5} />
              <div style={{textAlign: 'center', padding: 20}}>Select a country bubble or table row<br/>to view individual store prices.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- HELPERS ---
const StatBox = ({ label, value, color, theme }) => (
  <div style={{ background: 'rgba(255,255,255,0.03)', padding: 12, borderRadius: 8, border: `1px solid ${theme.border}` }}>
    <div style={{ fontSize: '0.65rem', color: theme.muted, marginBottom: 4, letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: color || theme.text }}>{value}</div>
  </div>
);

const generateHistogram = (offers) => {
  if (!offers.length) return [];
  const prices = offers.map(o => o.usdPrice);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const bins = 8;
  const step = (max - min) / bins || 100;
  
  const data = Array.from({ length: bins }, (_, i) => ({
    range: `$${Math.round(min + i * step)}`,
    count: 0
  }));

  prices.forEach(p => {
    const idx = Math.min(Math.floor((p - min) / step), bins - 1);
    data[idx].count++;
  });
  return data;
};

const getFlag = (code) => ({
  'US': '🇺🇸', 'BR': '🇧🇷', 'FR': '🇫🇷', 'DE': '🇩🇪', 'GB': '🇬🇧', 'ID': '🇮🇩', 'JP': '🇯🇵'
}[code] || '🌐');

const getCoords = (code) => ({
  'US': [-95.7, 37.0], 'BR': [-51.9, -14.2], 'FR': [2.2, 46.2], 
  'DE': [10.4, 51.1], 'GB': [-3.4, 55.3], 'ID': [113.9, -0.7], 'JP': [138.2, 36.2]
}[code] || [0, 0]);

export default FreeShopIndicator;